import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  IconButton,
  Divider,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Storage as DatabaseIcon,
  Computer as SystemIcon,
  Speed as PerformanceIcon
} from '@mui/icons-material';
import { useHealth } from '../contexts/HealthContext';

const HealthDetailModal = ({ open, onClose, environmentId = null }) => {
  const { 
    checkCurrentEnvironmentHealthDetailed, 
    getCurrentEnvironmentDisplay, 
    getDetailedEnvironmentHealth,
    getHealthDisplay 
  } = useHealth();
  const [detailedData, setDetailedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const intervalRef = useRef(null);
  
  // Gebruik specific environment of current environment
  const healthDisplay = environmentId ? getHealthDisplay(environmentId) : getCurrentEnvironmentDisplay();

  const fetchDetailedHealth = async () => {
    if (!open) return;
    
    setLoading(true);
    try {
      console.log('Fetching detailed health for environment:', environmentId || 'current');
      
      let detailed;
      if (environmentId) {
        // Fetch detailed info voor specifiek environment
        detailed = await getDetailedEnvironmentHealth(environmentId);
      } else {
        // Fetch detailed info voor current environment
        detailed = await checkCurrentEnvironmentHealthDetailed();
      }
      
      console.log('Detailed health result:', detailed);
      setDetailedData(detailed?.data || null);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error fetching detailed health:', error);
      setDetailedData(null);
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh elke 5 seconden
  useEffect(() => {
    if (!open || !autoRefresh) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Eerste load direct
    fetchDetailedHealth();

    // Start interval voor auto-refresh
    intervalRef.current = setInterval(() => {
      fetchDetailedHealth();
    }, 5000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [open, autoRefresh]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const handleClose = () => {
    console.log('Closing health detail modal');
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setDetailedData(null);
    setLastUpdate(null);
    onClose();
  };

  const handleManualRefresh = () => {
    fetchDetailedHealth();
  };

  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
  };

  const formatBytes = (bytes) => {
    if (!bytes) return 'N/A';
    return `${bytes} GB`;
  };

  const formatUptime = (uptime) => {
    if (!uptime) return 'Onbekend';
    return uptime;
  };

  const getStatusIcon = () => {
    switch (healthDisplay.status) {
      case 'healthy':
        return '✅';
      case 'warning':
      case 'degraded':
        return '⚠️';
      case 'unhealthy':
      case 'unreachable':
        return '❌';
      default:
        return '❓';
    }
  };

  const getProgressColor = (percentage) => {
    if (percentage < 60) return 'success';
    if (percentage < 80) return 'warning';
    return 'error';
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '80vh', maxHeight: '800px' }
      }}
      onEnter={() => console.log('Modal opening...')}
      onEntered={() => console.log('Modal opened!')}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <InfoIcon color="primary" />
            <Typography variant="h6">
              Environment Health Details {getStatusIcon()}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton
              onClick={toggleAutoRefresh}
              color={autoRefresh ? 'primary' : 'default'}
              title={autoRefresh ? 'Auto-refresh uitschakelen' : 'Auto-refresh inschakelen'}
            >
              {autoRefresh ? <PauseIcon /> : <PlayIcon />}
            </IconButton>
            <IconButton onClick={handleManualRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        {autoRefresh && (
          <Box mt={1}>
            <Typography variant="caption" color="text.secondary">
              🔄 Auto-refresh actief (elke 5 seconden)
              {lastUpdate && ` • Laatste update: ${lastUpdate.toLocaleTimeString('nl-NL')}`}
            </Typography>
          </Box>
        )}
      </DialogTitle>

      <DialogContent>
        {loading && !detailedData && (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Health informatie ophalen...
            </Typography>
          </Box>
        )}

        {!loading && !detailedData && (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <Typography variant="body2" color="text.secondary">
              Geen health data beschikbaar. Probeer opnieuw.
            </Typography>
          </Box>
        )}

        {detailedData && (
          <Grid container spacing={3}>
            {/* Status Overview */}
            <Grid item xs={12}>
              <Card elevation={2}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <InfoIcon color="primary" />
                    <Typography variant="h6">Status Overview</Typography>
                    {loading && <CircularProgress size={16} />}
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Chip
                        label={healthDisplay.label}
                        color={
                          healthDisplay.color === 'success.main' ? 'success' :
                          healthDisplay.color === 'warning.main' ? 'warning' :
                          healthDisplay.color === 'error.main' ? 'error' : 'default'
                        }
                        size="large"
                        sx={{ fontSize: '1rem', height: '40px' }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        Database: {detailedData.database_connected ? '✅ Verbonden' : '❌ Offline'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        Versie: {detailedData.version || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        Uptime: {formatUptime(detailedData.uptime)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Database Info */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <DatabaseIcon color="primary" />
                    <Typography variant="h6">Database</Typography>
                  </Box>
                  {detailedData.database_stats && (
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Totaal gebruikers:</strong> {detailedData.database_stats.total_users?.toLocaleString('nl-NL') || 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Audit logs:</strong> {detailedData.database_stats.total_audit_logs?.toLocaleString('nl-NL') || 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Laatste activiteit:</strong><br />
                        {detailedData.database_stats.last_activity
                          ? new Date(detailedData.database_stats.last_activity).toLocaleString('nl-NL')
                          : 'Geen recente activiteit'}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* System Resources */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <SystemIcon color="primary" />
                    <Typography variant="h6">Systeembronnen</Typography>
                  </Box>
                  {detailedData.system_info && (
                    <Box>
                      {/* CPU */}
                      <Box mb={2}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>CPU Gebruik: {detailedData.system_info.cpu_usage_percent?.toFixed(1) || '0'}%</strong>
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={detailedData.system_info.cpu_usage_percent || 0}
                          color={getProgressColor(detailedData.system_info.cpu_usage_percent || 0)}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>

                      {/* Memory */}
                      <Box mb={2}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Memory: {detailedData.system_info.memory_usage_percent?.toFixed(1) || '0'}%</strong>
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            ({formatBytes(detailedData.system_info.memory_used_gb)} / {formatBytes(detailedData.system_info.memory_total_gb)})
                          </Typography>
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={detailedData.system_info.memory_usage_percent || 0}
                          color={getProgressColor(detailedData.system_info.memory_usage_percent || 0)}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>

                      {/* Disk */}
                      <Box mb={2}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Disk: {detailedData.system_info.disk_usage_percent?.toFixed(1) || '0'}%</strong>
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            ({formatBytes(detailedData.system_info.disk_used_gb)} / {formatBytes(detailedData.system_info.disk_total_gb)})
                          </Typography>
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={detailedData.system_info.disk_usage_percent || 0}
                          color={getProgressColor(detailedData.system_info.disk_usage_percent || 0)}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>

                      <Typography variant="body2" color="text.secondary">
                        Python: {detailedData.system_info.python_version || 'N/A'}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Cache Performance */}
            {detailedData.database_stats?.cache_stats && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <PerformanceIcon color="primary" />
                      <Typography variant="h6">Cache Performance</Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2">
                          <strong>Actieve entries:</strong><br />
                          {detailedData.database_stats.cache_stats.valid_entries || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2">
                          <strong>Totaal entries:</strong><br />
                          {detailedData.database_stats.cache_stats.total_entries || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2">
                          <strong>Verlopen entries:</strong><br />
                          {detailedData.database_stats.cache_stats.expired_entries || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2">
                          <strong>Cache efficiency:</strong><br />
                          {detailedData.database_stats.cache_stats.total_entries > 0
                            ? `${Math.round((detailedData.database_stats.cache_stats.valid_entries / detailedData.database_stats.cache_stats.total_entries) * 100)}%`
                            : '0%'}
                        </Typography>
                      </Grid>
                    </Grid>
                    {detailedData.database_stats.cache_stats.cache_keys?.length > 0 && (
                      <Box mt={2}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Gecachte keys:</strong>
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {detailedData.database_stats.cache_stats.cache_keys.map((key, index) => (
                            <Chip
                              key={index}
                              label={key}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>

      <DialogActions>
        <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
          {autoRefresh ? '🔄 Automatisch vernieuwen elke 5 seconden' : '⏸️ Auto-refresh gepauzeerd'}
        </Typography>
        <Button onClick={handleClose} variant="contained">
          Sluiten
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HealthDetailModal; 